import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import {
  Zap, Clock, Calendar, AlertTriangle, RefreshCw, Pencil, X, Save, RotateCcw,
} from 'lucide-react';

const COLOR_STYLES = {
  red:    { dot: 'bg-red-500',    text: 'text-red-600',    chip: 'bg-red-50 text-red-700 border-red-200' },
  orange: { dot: 'bg-orange-500', text: 'text-orange-600', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
  amber:  { dot: 'bg-amber-500',  text: 'text-amber-600',  chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  green:  { dot: 'bg-green-500',  text: 'text-green-600',  chip: 'bg-green-50 text-green-700 border-green-200' },
};

// Pill colours for a task sitting on hold / paused / in query.
const STATE_STYLES = {
  red:    'bg-red-50 text-red-700 border-red-300 hover:bg-red-100',
  amber:  'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100',
  purple: 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100',
};

/**
 * Reason badge shown BEFORE the countdown when the current task is on hold,
 * paused or in query. Rendered as a button: the reason is often long, so the
 * pill shows the state and the full reason appears on click / hover.
 */
const StateBadge = ({ badge }) => {
  const [open, setOpen] = useState(false);
  if (!badge) return null;
  const cls = STATE_STYLES[badge.color] || STATE_STYLES.amber;
  const when = badge.at ? new Date(badge.at).toLocaleString() : '';
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border ${cls}`}
        title={badge.reason}
      >
        {badge.label}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left">
          <p className="text-xs font-semibold text-gray-800 mb-1">
            {badge.label}{badge.task_name ? ` — ${badge.task_name}` : ''}
          </p>
          <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">{badge.reason}</p>
          {(badge.by_name || when) && (
            <p className="text-[11px] text-gray-400 mt-2">
              {badge.by_name}{badge.by_name && when ? ' · ' : ''}{when}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The reason text, printed inline under the client line so it is readable at a
 * glance without hovering. The badge pill beside the countdown stays as the
 * compact status marker.
 */
const STATE_TEXT = {
  red:    'text-red-700',
  amber:  'text-amber-800',
  purple: 'text-purple-700',
};

// Backend sends this sentinel when the hold predates reason capture.
const NO_REASON = 'No reason recorded';

const StateReason = ({ badge }) => {
  if (!badge) return null;
  const colour = STATE_TEXT[badge.color] || STATE_TEXT.amber;
  const when = badge.at ? new Date(badge.at).toLocaleString() : '';
  const hasReason = badge.reason && badge.reason !== NO_REASON;
  return (
    <div className={`text-xs mt-0.5 ${colour}`}>
      <span className="font-semibold">
        {badge.label}{badge.task_name ? ` (${badge.task_name})` : ''}:
      </span>{' '}
      {hasReason ? (
        <span className="break-words">{badge.reason}</span>
      ) : (
        <span className="italic opacity-70">no reason recorded</span>
      )}
      {(badge.by_name || when) && (
        <span className="text-gray-400 font-normal">
          {' '}— {badge.by_name}{badge.by_name && when ? ', ' : ''}{when}
        </span>
      )}
    </div>
  );
};

// Compute remaining seconds to a UTC ISO cutoff, relative to now.
const secondsLeft = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((t - Date.now()) / 1000);
};

const colorFor = (secs) => {
  if (secs == null) return 'green';
  if (secs < 0) return 'red';
  if (secs <= 3600) return 'orange';
  if (secs <= 4 * 3600) return 'amber';
  return 'green';
};

const formatCountdown = (secs) => {
  if (secs == null) return '—';
  const overdue = secs < 0;
  let s = Math.abs(secs);
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  parts.push(`${h}h`, `${m}m`);
  if (!d) parts.push(`${s}s`);
  return (overdue ? 'Overdue ' : '') + parts.join(' ');
};

// IST "today" / "tomorrow" labels for a YYYY-MM-DD bucket date.
const istTodayStr = () => {
  const nowIst = new Date(Date.now() + (new Date().getTimezoneOffset() * 60000) + (5.5 * 3600000));
  return nowIst.toISOString().slice(0, 10);
};

const dateLabel = (bucketDate) => {
  const today = istTodayStr();
  const todayD = new Date(today + 'T00:00:00');
  const tgt = new Date(bucketDate + 'T00:00:00');
  const diff = Math.round((tgt - todayD) / 86400000);
  let rel = '';
  if (diff === 0) rel = 'Today';
  else if (diff === 1) rel = 'Tomorrow';
  else if (diff === -1) rel = 'Yesterday';
  else if (diff < 0) rel = `${Math.abs(diff)} days ago`;
  else rel = `In ${diff} days`;
  const pretty = tgt.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${rel} · ${pretty}`;
};

// UTC ISO -> value for <input type="datetime-local"> in the browser's local tz.
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const off = dt.getTimezoneOffset() * 60000;
  return new Date(dt.getTime() - off).toISOString().slice(0, 16);
};

const MANAGER_ROLES = ['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'];

const PriorityProjects = () => {
  const { token, user } = useAuth();
  const canEdit = !!user && ['admin', 'pm'].includes(user.role);
  const isManager = !!user && MANAGER_ROLES.includes(user.role);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [, setTick] = useState(0); // forces countdown re-render
  const [editTarget, setEditTarget] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const firstLoad = useRef(true);

  // Manager filters
  const [filterClient, setFilterClient] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Assign dialog (managers)
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignTask, setAssignTask] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSel, setAssignSel] = useState('');
  // Skill-matched users held back because they are on a different team — used to
  // explain an unexpectedly short (or empty) list.
  const [excludedByTeam, setExcludedByTeam] = useState(0);

  const load = useCallback(async () => {
    if (firstLoad.current) setLoading(true); else setRefreshing(true);
    try {
      const params = {};
      if (filterClient.trim()) params.client = filterClient.trim();
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const res = await api.get('/priority-projects', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setProjects(Array.isArray(res.data?.projects) ? res.data.projects : []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load priority projects');
    } finally {
      setLoading(false);
      setRefreshing(false);
      firstLoad.current = false;
    }
  }, [token, filterClient, filterFrom, filterTo]);

  useEffect(() => { load(); }, [load]);

  const openAssign = async (p) => {
    setAssignTarget(p);
    setAssignUsers([]);
    setAssignTask(null);
    setAssignSel('');
    setExcludedByTeam(0);
    setAssignLoading(true);
    try {
      const res = await api.get(`/priority-projects/${p.id}/eligible-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignTask(res.data?.task || null);
      setAssignUsers(res.data?.users || []);
      setExcludedByTeam(res.data?.excluded_by_team || 0);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load eligible users');
    } finally {
      setAssignLoading(false);
    }
  };

  const doAssign = async () => {
    if (!assignTarget || !assignSel) return;
    try {
      await api.post(`/priority-projects/${assignTarget.id}/assign`, { user_id: assignSel }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignTarget(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to assign');
    }
  };

  const takeForMe = async (p) => {
    if (!window.confirm(`Take "${p.my_task?.name}" on ${p.job_code || p.title}?`)) return;
    try {
      await api.post(`/priority-projects/${p.id}/assign`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
      alert('Task assigned to you. Start it from your projects.');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to take task');
    }
  };

  // Live countdown tick every second.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const openEdit = (p) => {
    setEditTarget(p);
    setEditValue(isoToLocalInput(p.cutoff_datetime));
  };
  const closeEdit = () => { setEditTarget(null); setEditValue(''); };

  const saveBucket = async (clear = false) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const payload = { cutoff_datetime: clear ? null : new Date(editValue).toISOString() };
      await api.put(`/pm/projects/${editTarget.id}/cutoff`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeEdit();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update bucket');
    } finally {
      setSaving(false);
    }
  };

  // Group: bucket_date -> bucket_time -> [projects]
  const groups = {};
  for (const p of projects) {
    const d = p.bucket_date || 'unknown';
    const t = p.bucket_time || '--:--';
    groups[d] = groups[d] || {};
    groups[d][t] = groups[d][t] || [];
    groups[d][t].push(p);
  }
  // Newest date first: Today → Yesterday → older (descending by YYYY-MM-DD).
  // 'unknown' buckets sort to the very end.
  const sortedDates = Object.keys(groups).sort((a, b) => {
    if (a === 'unknown') return 1;
    if (b === 'unknown') return -1;
    return b.localeCompare(a);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading priority projects…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Priority Projects</h1>
            <p className="text-sm text-gray-500">Projects bucketed by client cutoff time — complete each job before its cutoff.</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {isManager && (
        <div className="mb-4 flex flex-wrap items-end gap-3 bg-white border border-gray-200 rounded-xl p-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Client</label>
            <input
              type="text"
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              placeholder="Client name"
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-48"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">From (cutoff date)</label>
            <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">To</label>
            <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
          </div>
          <button onClick={load} className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Apply</button>
          {(filterClient || filterFrom || filterTo) && (
            <button onClick={() => { setFilterClient(''); setFilterFrom(''); setFilterTo(''); }} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">Clear</button>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Normal user: skill-matched grabbable jobs */}
      {!isManager && (
        projects.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
            <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            No projects available for your skills right now.
            <p className="text-xs mt-1">Jobs appear here when their next task matches your skill mapping and is ready to start.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {projects.map((p) => {
              const secs = secondsLeft(p.cutoff_datetime);
              const cs = COLOR_STYLES[colorFor(secs)] || COLOR_STYLES.green;
              return (
                <div key={p.id} className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${cs.dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 fl-wrap">
                      <span className="font-medium text-gray-800 truncate">{p.job_code ? `#${p.job_code}` : (p.title || 'Untitled')}</span>
                      <span className="text-xs text-gray-500 truncate">{p.title}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {p.client || 'Unknown client'}{p.complexity ? ` · ${p.complexity}` : ''}
                      {p.my_task && <span className="ml-1 text-blue-700 font-medium">· {p.my_task.name}</span>}
                      {p.my_task?.assigned_to && !p.my_task?.assigned_to_me && (
                        <span className="ml-1 text-amber-600">(assigned, not started)</span>
                      )}
                    </div>
                  </div>
                  <StateBadge badge={p.state_badge} />
                  {p.cutoff_datetime && (
                    <span className={`text-sm font-semibold tabular-nums shrink-0 ${cs.text}`}>{formatCountdown(secs)}</span>
                  )}
                  <button
                    onClick={() => takeForMe(p)}
                    className="shrink-0 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >Assign to me</button>
                </div>
              );
            })}
          </div>
        )
      )}

      {isManager && (projects.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
          <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          No active projects fall under a client cutoff rule yet.
          <p className="text-xs mt-1">Configure client cutoff times in Settings → Cutoff Workflow.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {sortedDates.map((date) => {
            const times = groups[date];
            const sortedTimes = Object.keys(times).sort();
            const dayCount = sortedTimes.reduce((n, t) => n + times[t].length, 0);
            return (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{dateLabel(date)}</h2>
                  <span className="text-xs text-gray-400">({dayCount})</span>
                </div>
                <div className="space-y-4">
                  {sortedTimes.map((t) => (
                    <div key={t} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-gray-800">{t} cutoff</span>
                        <span className="text-xs text-gray-400">· {times[t].length} project{times[t].length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {times[t].map((p) => {
                          const secs = secondsLeft(p.cutoff_datetime);
                          const cs = COLOR_STYLES[colorFor(secs)] || COLOR_STYLES.green;
                          return (
                            <div key={p.id} className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50">
                              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${cs.dot}`} />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-gray-800 truncate">{p.title || 'Untitled'}</span>
                                  {p.job_code && <span className="text-xs text-gray-500">#{p.job_code}</span>}
                                  {p.is_override && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">OVERRIDE</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {p.client || 'Unknown client'} · {p.status}{p.complexity ? ` · ${p.complexity}` : ''}
                                </div>
                                {/* The reason is printed on the row itself — a held
                                    job is useless to a planner unless they can see
                                    WHY without hovering or clicking anything. */}
                                <StateReason badge={p.state_badge} />
                              </div>
                              <StateBadge badge={p.state_badge} />
                              <span className={`text-sm font-semibold tabular-nums shrink-0 ${cs.text}`}>
                                {formatCountdown(secs)}
                              </span>
                              {p.current_task?.can_assign ? (
                                <button
                                  onClick={() => openAssign(p)}
                                  className="shrink-0 px-2.5 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
                                  title={`Assign "${p.current_task?.name}" to a ${p.current_task?.team || 'skill'}-matched user`}
                                >Assign</button>
                              ) : (
                                <span
                                  className="shrink-0 px-2.5 py-1 text-xs rounded-md bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                                  title={p.current_task?.assign_block_reason || 'Not assignable'}
                                >{p.current_task?.assign_block_reason || 'In progress'}</span>
                              )}
                              {canEdit && (
                                <button
                                  onClick={() => openEdit(p)}
                                  className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                  title="Change bucket date/time"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Edit bucket modal (PM/Admin) */}
      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">Change Priority Bucket</h3>
              <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-800">{editTarget.title || 'Untitled'}</p>
                <p className="text-xs text-gray-500">{editTarget.client} {editTarget.job_code ? `· #${editTarget.job_code}` : ''}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cutoff date &amp; time (your local time)</label>
                <input
                  type="datetime-local"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Sets an explicit cutoff for this project. Reset to fall back to the client's default cutoff bucket.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => saveBucket(true)}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-red-600 disabled:opacity-50"
                title="Remove override and use client cutoff"
              >
                <RotateCcw className="w-4 h-4" /> Reset to client cutoff
              </button>
              <div className="flex items-center gap-2">
                <button onClick={closeEdit} disabled={saving} className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100">Cancel</button>
                <button
                  onClick={() => saveBucket(false)}
                  disabled={saving || !editValue}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign dialog (managers) */}
      {assignTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">Assign Task</h3>
              <button onClick={() => setAssignTarget(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-800">{assignTarget.job_code ? `#${assignTarget.job_code}` : assignTarget.title}</p>
                <p className="text-xs text-gray-500">{assignTarget.client} {assignTarget.complexity ? `· ${assignTarget.complexity}` : ''}</p>
              </div>
              {assignLoading ? (
                <div className="text-sm text-gray-500 py-4 text-center">Loading eligible users…</div>
              ) : !assignTask ? (
                <div className="text-sm text-gray-500">No assignable task in this project.</div>
              ) : (
                <>
                  <div className="text-sm">
                    Current task: <span className="font-medium text-gray-800">{assignTask.name}</span>
                    {assignTask.team && (
                      <span className="ml-1 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 uppercase">
                        {assignTask.team}
                      </span>
                    )}
                    {assignTask.assigned_to && (
                      <span className="text-amber-600"> · currently assigned{assignTask.assigned_to_name ? ` to ${assignTask.assigned_to_name}` : ''}</span>
                    )}
                  </div>
                  {assignTask.can_assign === false && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-sm">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{assignTask.assign_block_reason}. This task cannot be reassigned while it is being worked on.</span>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Eligible {assignTask.team ? `${assignTask.team} ` : ''}user
                      <span className="text-gray-400"> — matching skill and team</span>
                    </label>
                    {assignUsers.length === 0 ? (
                      <p className="text-sm text-amber-600">
                        No {assignTask.team || ''} user has the matching skill for this task/complexity.
                        {excludedByTeam > 0 && ` ${excludedByTeam} skill-matched user${excludedByTeam !== 1 ? 's are' : ' is'} on another team.`}
                      </p>
                    ) : (
                      <select
                        value={assignSel}
                        onChange={(e) => setAssignSel(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                      >
                        <option value="">Select user…</option>
                        {assignUsers.map((u) => (
                          <option key={u.id} value={u.id}>{u.name} ({(u.role || '').replace(/_/g, ' ')})</option>
                        ))}
                      </select>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button onClick={() => setAssignTarget(null)} className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100">Cancel</button>
              <button
                onClick={doAssign}
                disabled={!assignSel}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
              >Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriorityProjects;
